// Step 1.5: Client Review - Review and optionally edit client before classification

import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useVisitWizard } from '../../../../contexts/VisitWizardContext'
import { useUpdateClient } from '../../../../hooks/useClients'
import { toast } from '../../../../lib/toast'
import { Button } from '../../../../components/ui/button'
import { Input } from '../../../../components/ui/input'
import { Label } from '../../../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card'
import { Edit, Save, X, ArrowRight, User, MapPin, Phone, Info } from 'lucide-react'
import { DatePickerField } from '../../../../components/ui/date-picker'
import { PhoneInput } from '../../../../components/ui/phone-input'
import { calculateAge } from '../../../../lib/utils'
import {
  RELIGIONS,
  EDUCATIONAL_ATTAINMENT,
  INDIGENOUS_GROUPS,
  CITIZENSHIPS,
  GENDERS,
  CIVIL_STATUS_OPTIONS,
} from '../../../../config/client-dropdowns'
import { useProvinces, useCitiesByProvince, useBarangaysByCity } from '../../../../hooks/useAddresses'

// Validation schema (same as ClientForm)
const clientSchema = z.object({
  firstname: z.string().min(1, 'First name is required').max(50),
  middlename: z.string().max(50).optional().nullable(),
  lastname: z.string().min(1, 'Last name is required').max(50),
  email: z.string().email('Invalid email').max(50).optional().nullable().or(z.literal('')),
  gender: z.enum(['Male', 'Female', 'Others'], {
    required_error: 'Gender is required',
  }),
  birthday: z.string().min(1, 'Date of birth is required'),
  monthly_income: z.number().min(0, 'Income must be positive').default(0),
  civil_status: z.string().min(1, 'Civil status is required').max(30),
  religion: z.string().max(100).optional().nullable(),
  religion_other: z.string().max(100).optional().nullable(), // For "Others" option
  citizenship: z.string().min(1, 'Citizenship is required').max(50),
  educational_attainment: z.string().min(1, 'Educational attainment is required').max(50),
  indigenous_group: z.string().max(50).optional().nullable(),
  indigenous_group_other: z.string().max(50).optional().nullable(), // For "Others" option
  street_name: z.string().max(50).optional().nullable(),
  barangay: z.string().max(50).optional().nullable(),
  city: z.string().min(1, 'City is required').max(50),
  province: z.string().min(1, 'Province is required').max(50),
  postal_code: z.string().length(4, 'Postal code must be 4 digits').regex(/^\d+$/, 'Must be numeric').optional().nullable().or(z.literal('')),
  contact_number: z.string().optional().nullable().or(z.literal('')),
})

type ClientFormData = z.infer<typeof clientSchema>

export function ClientReview() {
  const { state, goToStep, dispatch } = useVisitWizard()
  const client = state.selectedClient!
  const [isEditing, setIsEditing] = useState(false)
  const updateClient = useUpdateClient()

  // State for cascading address dropdowns
  const [selectedProvinceId, setSelectedProvinceId] = useState<number | null>(null)
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null)

  // Fetch address data
  const { data: provinces } = useProvinces()
  const { data: cities } = useCitiesByProvince(selectedProvinceId)
  const { data: barangays } = useBarangaysByCity(selectedCityId)

  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      firstname: '',
      middlename: '',
      lastname: '',
      email: '',
      gender: undefined,
      birthday: '',
      monthly_income: 0,
      civil_status: '',
      religion: '',
      religion_other: '',
      citizenship: 'Filipino',
      educational_attainment: '',
      indigenous_group: '',
      indigenous_group_other: '',
      street_name: '',
      barangay: '',
      city: '',
      province: '',
      postal_code: '',
      contact_number: '',
    },
  })

  // Destructure reset for proper useEffect dependency
  const { reset: resetForm, watch } = form

  // Watch for "Others" selections
  const religionValue = watch('religion')
  const indigenousGroupValue = watch('indigenous_group')
  const showReligionOther = religionValue === 'Others'
  const showIndigenousOther = indigenousGroupValue === 'Others'

  // Initialize province ID when entering edit mode
  useEffect(() => {
    if (provinces && client.province && isEditing) {
      const province = provinces.find((p) => p.province_name === client.province)
      if (province && selectedProvinceId !== province.id) {
        setSelectedProvinceId(province.id)
      }
    }
  }, [provinces, client.province, isEditing, selectedProvinceId])

  // Initialize city ID when entering edit mode and province is set
  useEffect(() => {
    if (cities && client.city && isEditing && selectedProvinceId) {
      const city = cities.find((c) => c.city_municipality_name === client.city)
      if (city && selectedCityId !== city.id) {
        setSelectedCityId(city.id)
      }
    }
  }, [cities, client.city, isEditing, selectedProvinceId, selectedCityId])

  const onSave = async (data: ClientFormData) => {
    try {
      // Handle "Others" fields - merge into main field if "Others" was selected
      const processedData = { ...data }

      if (data.religion === 'Others' && data.religion_other) {
        processedData.religion = data.religion_other
      }

      if (data.indigenous_group === 'Others' && data.indigenous_group_other) {
        processedData.indigenous_group = data.indigenous_group_other
      }

      // Remove the "_other" helper fields before submission
      delete (processedData as any).religion_other
      delete (processedData as any).indigenous_group_other

      // Convert empty strings to null
      const cleanData = Object.fromEntries(
        Object.entries(processedData).map(([key, value]) => [key, value === '' ? null : value])
      ) as any

      await updateClient.mutateAsync({ id: client.id, data: cleanData })

      // Update the client in wizard state
      dispatch({
        type: 'SELECT_CLIENT',
        payload: { ...client, ...cleanData }
      })

      toast.success('Client updated successfully!')
      setIsEditing(false)
    } catch (error: any) {
      toast.error('Failed to update client', error.response?.data?.detail || error.message)
    }
  }

  const handleContinue = () => {
    goToStep('classification')
  }

  const age = calculateAge(client.birthday)

  if (!isEditing) {
    // Read-only view with Edit button
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-primary">Review Client Information</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Review the client's information. You can edit if needed before proceeding.
            </p>
          </div>
          <Button
            onClick={() => {
              // Reset form with client data before entering edit mode
              resetForm({
                firstname: client.firstname || '',
                middlename: client.middlename || '',
                lastname: client.lastname || '',
                email: client.email || '',
                gender: client.gender as any,
                birthday: client.birthday || '',
                monthly_income: client.monthly_income || 0,
                civil_status: client.civil_status || '',
                religion: client.religion || '',
                religion_other: '',
                citizenship: client.citizenship || 'Filipino',
                educational_attainment: client.educational_attainment || '',
                indigenous_group: client.indigenous_group || '',
                indigenous_group_other: '',
                street_name: client.street_name || '',
                barangay: client.barangay || '',
                city: client.city || '',
                province: client.province || '',
                postal_code: client.postal_code || '',
                contact_number: client.contact_number || '',
              })

              // Initialize province and city IDs for cascading dropdowns
              if (provinces && client.province) {
                const province = provinces.find((p) => p.province_name === client.province)
                if (province) {
                  setSelectedProvinceId(province.id)
                }
              }

              setIsEditing(true)
            }}
            variant="outline"
            className="gap-2"
          >
            <Edit className="h-4 w-4" />
            Edit Client
          </Button>
        </div>

        {/* Client Information Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-primary">
                <User className="h-5 w-5" />
                Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoRow label="Name" value={`${client.firstname} ${client.middlename || ''} ${client.lastname}`.trim()} />
              <InfoRow label="Gender" value={client.gender || 'N/A'} />
              <InfoRow label="Age" value={age ? `${age} years` : 'N/A'} />
              <InfoRow label="Birthday" value={client.birthday || 'N/A'} />
              <InfoRow label="Civil Status" value={client.civil_status || 'N/A'} />
              <InfoRow label="Citizenship" value={client.citizenship || 'N/A'} />
              <InfoRow label="Religion" value={client.religion || 'N/A'} />
              <InfoRow label="Educational Attainment" value={client.educational_attainment || 'N/A'} />
              <InfoRow label="Indigenous Group" value={client.indigenous_group || 'N/A'} />
            </CardContent>
          </Card>

          {/* Contact & Address */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-primary">
                <MapPin className="h-5 w-5" />
                Contact & Address
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoRow label="Email" value={client.email || 'N/A'} />
              <InfoRow label="Contact Number" value={client.contact_number || 'N/A'} />
              <InfoRow label="Street" value={client.street_name || 'N/A'} />
              <InfoRow label="Barangay" value={client.barangay || 'N/A'} />
              <InfoRow label="City" value={client.city || 'N/A'} />
              <InfoRow label="Province" value={client.province || 'N/A'} />
              <InfoRow label="Postal Code" value={client.postal_code || 'N/A'} />
            </CardContent>
          </Card>

          {/* Other Information */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-primary">
                <Info className="h-5 w-5" />
                Other Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <InfoRow
                label="Monthly Income"
                value={client.monthly_income ? `₱${client.monthly_income.toLocaleString()}` : 'N/A'}
              />
            </CardContent>
          </Card>
        </div>

        {/* Continue Button */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={handleContinue}
            className="bg-primary hover:bg-primary/90 gap-2"
            size="lg"
          >
            Continue to Classification
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    )
  }

  // Edit mode - show full form with all fields
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-primary">Edit Client Information</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Update any client information as needed
          </p>
        </div>
        <Button
          onClick={() => setIsEditing(false)}
          variant="outline"
          className="gap-2"
        >
          <X className="h-4 w-4" />
          Cancel
        </Button>
      </div>

      <form onSubmit={form.handleSubmit(onSave)} className="space-y-6">
        {/* Personal Information Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary">
              <User className="h-5 w-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* First Name */}
            <div className="space-y-2">
              <Label htmlFor="firstname">First Name *</Label>
              <Input id="firstname" {...form.register('firstname')} />
              {form.formState.errors.firstname && (
                <p className="text-sm text-red-500">{form.formState.errors.firstname.message}</p>
              )}
            </div>

            {/* Middle Name */}
            <div className="space-y-2">
              <Label htmlFor="middlename">Middle Name</Label>
              <Input id="middlename" {...form.register('middlename')} />
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <Label htmlFor="lastname">Last Name *</Label>
              <Input id="lastname" {...form.register('lastname')} />
              {form.formState.errors.lastname && (
                <p className="text-sm text-red-500">{form.formState.errors.lastname.message}</p>
              )}
            </div>

            {/* Gender */}
            <div className="space-y-2">
              <Label htmlFor="gender">Gender *</Label>
              <Select value={watch('gender')} onValueChange={(value) => form.setValue('gender', value as any)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  {GENDERS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.gender && (
                <p className="text-sm text-red-500">{form.formState.errors.gender.message}</p>
              )}
            </div>

            {/* Birthday */}
            <div className="space-y-2">
              <Label htmlFor="birthday">Date of Birth *</Label>
              <DatePickerField
                value={watch('birthday')}
                onChange={(value) => form.setValue('birthday', value || '')}
                placeholder="Select date of birth"
              />
              {form.formState.errors.birthday && (
                <p className="text-sm text-red-500">{form.formState.errors.birthday.message}</p>
              )}
            </div>

            {/* Civil Status */}
            <div className="space-y-2">
              <Label htmlFor="civil_status">Civil Status *</Label>
              <Select value={watch('civil_status')} onValueChange={(value) => form.setValue('civil_status', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select civil status" />
                </SelectTrigger>
                <SelectContent>
                  {CIVIL_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.civil_status && (
                <p className="text-sm text-red-500">{form.formState.errors.civil_status.message}</p>
              )}
            </div>

            {/* Citizenship */}
            <div className="space-y-2">
              <Label htmlFor="citizenship">Citizenship *</Label>
              <Select value={watch('citizenship')} onValueChange={(value) => form.setValue('citizenship', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select citizenship" />
                </SelectTrigger>
                <SelectContent>
                  {CITIZENSHIPS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.citizenship && (
                <p className="text-sm text-red-500">{form.formState.errors.citizenship.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contact Information Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary">
              <Phone className="h-5 w-5" />
              Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input type="email" id="email" {...form.register('email')} />
              {form.formState.errors.email && (
                <p className="text-sm text-red-500">{form.formState.errors.email.message}</p>
              )}
            </div>

            {/* Contact Number */}
            <div className="space-y-2">
              <Label htmlFor="contact_number">Contact Number</Label>
              <PhoneInput
                value={watch('contact_number') || ''}
                onChange={(value) => form.setValue('contact_number', value || '')}
                defaultCountry="PH"
                placeholder="Enter phone number"
              />
              {form.formState.errors.contact_number && (
                <p className="text-sm text-red-500">{form.formState.errors.contact_number.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Address Information Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary">
              <MapPin className="h-5 w-5" />
              Address Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Street Name */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="street_name">Street/House Number</Label>
              <Input id="street_name" {...form.register('street_name')} />
            </div>

            {/* Province */}
            <div className="space-y-2">
              <Label htmlFor="province">Province *</Label>
              <Select
                value={selectedProvinceId?.toString() || undefined}
                onValueChange={(value) => {
                  const provinceId = parseInt(value)
                  setSelectedProvinceId(provinceId)
                  setSelectedCityId(null) // Reset city and barangay when province changes
                  const province = provinces?.find(p => p.id === provinceId)
                  form.setValue('province', province?.province_name || '')
                  form.setValue('city', '') // Reset dependent fields
                  form.setValue('barangay', '')
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select province" />
                </SelectTrigger>
                <SelectContent>
                  {provinces?.map((province) => (
                    <SelectItem key={province.id} value={province.id.toString()}>
                      {province.province_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.province && (
                <p className="text-sm text-red-500">{form.formState.errors.province.message}</p>
              )}
            </div>

            {/* City */}
            <div className="space-y-2">
              <Label htmlFor="city">City/Municipality *</Label>
              <Select
                value={selectedCityId?.toString() || undefined}
                onValueChange={(value) => {
                  const cityId = parseInt(value)
                  setSelectedCityId(cityId)
                  const city = cities?.find(c => c.id === cityId)
                  form.setValue('city', city?.city_municipality_name || '')
                  form.setValue('barangay', '') // Reset barangay when city changes
                }}
                disabled={!selectedProvinceId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !selectedProvinceId
                      ? "Select province first"
                      : "Select city/municipality"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {cities?.map((city) => (
                    <SelectItem key={city.id} value={city.id.toString()}>
                      {city.city_municipality_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.city && (
                <p className="text-sm text-red-500">{form.formState.errors.city.message}</p>
              )}
            </div>

            {/* Barangay */}
            <div className="space-y-2">
              <Label htmlFor="barangay">Barangay</Label>
              <Select
                value={watch('barangay') || undefined}
                onValueChange={(value) => {
                  const barangay = barangays?.find(b => b.barangay_name === value)
                  form.setValue('barangay', barangay?.barangay_name || value)
                }}
                disabled={!selectedCityId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !selectedCityId
                      ? "Select city first"
                      : "Select barangay"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {barangays?.map((barangay) => (
                    <SelectItem key={barangay.id} value={barangay.barangay_name}>
                      {barangay.barangay_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Postal Code */}
            <div className="space-y-2">
              <Label htmlFor="postal_code">Postal Code</Label>
              <Input
                id="postal_code"
                {...form.register('postal_code')}
                placeholder="1000"
                maxLength={4}
              />
              {form.formState.errors.postal_code && (
                <p className="text-sm text-red-500">{form.formState.errors.postal_code.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Other Information Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary">
              <Info className="h-5 w-5" />
              Other Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Religion */}
            <div className="space-y-2">
              <Label htmlFor="religion">Religion</Label>
              <Select value={watch('religion') || undefined} onValueChange={(value) => form.setValue('religion', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select religion" />
                </SelectTrigger>
                <SelectContent>
                  {RELIGIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Religion Other (conditional) */}
            {showReligionOther && (
              <div className="space-y-2">
                <Label htmlFor="religion_other">
                  Please Specify Religion <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="religion_other"
                  {...form.register('religion_other')}
                  placeholder="Please specify"
                />
              </div>
            )}

            {/* Educational Attainment */}
            <div className="space-y-2">
              <Label htmlFor="educational_attainment">Educational Attainment *</Label>
              <Select value={watch('educational_attainment')} onValueChange={(value) => form.setValue('educational_attainment', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select educational attainment" />
                </SelectTrigger>
                <SelectContent>
                  {EDUCATIONAL_ATTAINMENT.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.educational_attainment && (
                <p className="text-sm text-red-500">{form.formState.errors.educational_attainment.message}</p>
              )}
            </div>

            {/* Indigenous Group */}
            <div className="space-y-2">
              <Label htmlFor="indigenous_group">Indigenous Group</Label>
              <Select value={watch('indigenous_group') || undefined} onValueChange={(value) => form.setValue('indigenous_group', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select indigenous group (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {INDIGENOUS_GROUPS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Indigenous Group Other (conditional) */}
            {showIndigenousOther && (
              <div className="space-y-2">
                <Label htmlFor="indigenous_group_other">
                  Please Specify Indigenous Group <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="indigenous_group_other"
                  {...form.register('indigenous_group_other')}
                  placeholder="Please specify"
                />
              </div>
            )}

            {/* Monthly Income */}
            <div className="space-y-2">
              <Label htmlFor="monthly_income">Monthly Income (PHP) *</Label>
              <Input
                type="number"
                step="0.01"
                id="monthly_income"
                {...form.register('monthly_income', { valueAsNumber: true })}
                placeholder="0.00"
              />
              {form.formState.errors.monthly_income && (
                <p className="text-sm text-red-500">{form.formState.errors.monthly_income.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex gap-4 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsEditing(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="bg-primary hover:bg-primary/90 gap-2"
          >
            <Save className="h-4 w-4" />
            {form.formState.isSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  )
}

// Helper component for displaying info rows
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-2 border-b last:border-0">
      <span className="text-sm font-medium text-muted-foreground">{label}:</span>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  )
}
